import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { AITranslationService } from '../../services/aiTranslationService';
import TranslationErrorHandler, { TranslationErrorType } from '../../utils/translationErrorHandler';
import './ContentLanguageIndicator.css';

export interface ContentLanguageIndicatorProps {
  /** The language the content was created in */
  contentLanguage: 'en' | 'zh';
  
  /** The original content text */
  originalContent: string;
  
  /** Callback when translation is completed */
  onTranslated?: (translatedText: string) => void;
  
  /** Optional CSS class name */
  className?: string;
  
  /** Translation context for better AI translation */
  translationContext?: 'survey' | 'task' | 'general';
}

/**
 * ContentLanguageIndicator - Shows content language and provides translation functionality
 * 
 * Features:
 * - Shows language badge when content language differs from UI language
 * - Provides "Translate to [Language]" button
 * - Shows loading state during translation
 * - Shows "Translated from [Language]" badge after translation
 * - Handles translation errors gracefully
 */
export const ContentLanguageIndicator: React.FC<ContentLanguageIndicatorProps> = ({
  contentLanguage,
  originalContent,
  onTranslated,
  className = '',
  translationContext = 'general'
}) => {
  const { uiLanguage, t } = useLanguage();
  const [isTranslating, setIsTranslating] = useState(false);
  const [isTranslated, setIsTranslated] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);

  // Get language names
  const getLanguageName = (lang: 'en' | 'zh'): string => {
    return lang === 'en' ? 'English' : '中文';
  };

  // Check if translation is needed
  const needsTranslation = contentLanguage !== uiLanguage;

  // Handle translation
  const handleTranslate = async () => {
    if (!needsTranslation || isTranslating || isTranslated) {
      return;
    }

    setIsTranslating(true);
    setTranslationError(null);

    try {
      const result = await AITranslationService.translate(
        originalContent,
        contentLanguage,
        uiLanguage,
        translationContext
      );

      setIsTranslated(true);
      
      // Call the callback with translated text
      if (onTranslated) {
        onTranslated(result.translatedText);
      }
    } catch (error) {
      console.error('Translation failed:', error);
      
      // Get user-friendly error message
      const errorMessage = TranslationErrorHandler.getUserFriendlyMessage(
        TranslationErrorType.AI_TRANSLATION_FAILED
      );
      
      setTranslationError(errorMessage);
    } finally {
      setIsTranslating(false);
    }
  };

  // If content language matches UI language, show nothing
  if (!needsTranslation) {
    return null;
  }

  return (
    <div className={`content-language-indicator ${className}`}>
      {/* Language badge */}
      <div className="language-badge">
        <span className="language-icon">🌐</span>
        <span className="language-text">
          {isTranslated
            ? t('content.translatedFrom', { language: getLanguageName(contentLanguage) })
            : t('content.originalLanguage', { language: getLanguageName(contentLanguage) })
          }
        </span>
      </div>

      {/* Translation button or status */}
      {!isTranslated && (
        <button
          className="translate-button"
          onClick={handleTranslate}
          disabled={isTranslating}
          aria-label={t('content.translateTo', { language: getLanguageName(uiLanguage) })}
        >
          {isTranslating ? (
            <>
              <span className="loading-spinner" aria-hidden="true"></span>
              <span>{t('content.translating')}</span>
            </>
          ) : (
            <>
              <span className="translate-icon" aria-hidden="true">🔄</span>
              <span>{t('content.translateTo', { language: getLanguageName(uiLanguage) })}</span>
            </>
          )}
        </button>
      )}

      {/* Translation error */}
      {translationError && (
        <div className="translation-error">
          <span className="error-icon" aria-hidden="true">⚠️</span>
          <span>{t('content.translationError')}</span>
          <button
            className="retry-button"
            onClick={handleTranslate}
            aria-label={t('common.retry')}
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* Translated badge */}
      {isTranslated && (
        <div className="translated-badge">
          <span className="success-icon" aria-hidden="true">✓</span>
          <span>{t('content.translated')}</span>
        </div>
      )}
    </div>
  );
};

export default ContentLanguageIndicator;
